import { motion } from "framer-motion";
import {
  Instagram,
  Youtube,
  Twitter,
  Music2,
  Play,
  Calendar,
  ShoppingBag,
  ArrowUpRight,
  BadgeCheck,
} from "lucide-react";

const PERSONA_IMG =
  "https://images.unsplash.com/photo-1762289581607-fc292299dc87?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHw0fHxlbGVjdHJvbmljJTIwbXVzaWMlMjBwcm9kdWNlciUyMHBvcnRyYWl0fGVufDB8fHx8MTc4NjMwNzExMnww&ixlib=rb-4.1.0&q=85";
const PRODUCT_IMG =
  "https://images.unsplash.com/photo-1614613535308-eb5fbd3d2c17?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwxfHx2aW55bCUyMHJlY29yZCUyMGNvdmVyJTIwYXJ0fGVufDB8fHx8MTc4NjMwNzExMnww&ixlib=rb-4.1.0&q=85";
const VIDEO_IMG =
  "https://images.unsplash.com/photo-1670028514318-0ac718c0590d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwyfHxjb25jZXJ0JTIwc3RhZ2UlMjBsaWdodHN8ZW58MHx8fHwxNzg2MzA3MTEyfDA&ixlib=rb-4.1.0&q=85";

const WAVE = [40, 70, 30, 90, 55];

const parent = { hidden: {}, show: { transition: { staggerChildren: 0.07, delayChildren: 0.1 } } };
const child = {
  hidden: { opacity: 0, y: 14 },
  show: { opacity: 1, y: 0, transition: { duration: 0.42, ease: [0.22, 1, 0.36, 1] } },
};

const toName = (handle) => {
  const parts = (handle || "yourname").split(/[._-]+/).filter(Boolean);
  return parts.map((s) => s.toUpperCase()).join(" ") || "YOURNAME";
};

export const PhoneMockup = ({ handle = "yourname", buildKey = 0 }) => {
  const displayName = toName(handle);

  return (
    <div
      data-testid="phone-mockup"
      className="relative mx-auto w-[300px] rounded-[2.75rem] border-[10px] border-ink/90 bg-ink/90 shadow-lift sm:w-[330px]"
    >
      <div className="absolute left-1/2 top-2 z-20 h-6 w-24 -translate-x-1/2 rounded-full bg-ink/90" />

      <div className="relative h-[640px] overflow-hidden rounded-[2.1rem] bg-bg">
        {/* build progress line */}
        <motion.div
          key={`build-${buildKey}`}
          initial={{ scaleX: 0, opacity: 1 }}
          animate={{ scaleX: 1, opacity: [1, 1, 0] }}
          transition={{ duration: 0.85, ease: [0.22, 1, 0.36, 1], times: [0, 0.7, 1] }}
          className="absolute inset-x-0 top-0 z-30 h-[3px] origin-left bg-lime"
        />

        <div className="no-scrollbar h-full overflow-y-auto">
          {/* cover */}
          <div className="relative h-28 w-full bg-gradient-to-br from-lime/70 via-lime/30 to-surfaceAlt">
            <div className="absolute inset-0 opacity-40 [background:radial-gradient(circle_at_30%_120%,var(--accent),transparent_60%)]" />
          </div>

          <div className="px-4 pb-8">
            {/* header — updates live as you type */}
            <div className="-mt-10 flex items-end justify-between">
              <img
                src={PERSONA_IMG}
                alt={displayName}
                className="h-20 w-20 rounded-2xl border-4 border-bg object-cover"
                loading="lazy"
              />
              <div className="mb-1 flex gap-2">
                {[Instagram, Youtube, Twitter, Music2].map((Icon, i) => (
                  <span key={i} className="grid h-8 w-8 place-items-center rounded-full border border-line bg-surface text-ink">
                    <Icon size={14} />
                  </span>
                ))}
              </div>
            </div>

            <div className="mt-3 flex items-center gap-1.5">
              <h3 data-testid="preview-name" className="font-display text-lg font-semibold tracking-tight text-ink">
                {displayName}
              </h3>
              <BadgeCheck size={16} className="fill-lime text-ink" />
            </div>
            <p data-testid="preview-handle" className="text-xs font-medium text-lime">
              breel.ink/{handle}
            </p>
            <p className="mt-2 text-[13px] leading-relaxed text-ink">
              Sound architect · Berlin. Latest EP <span className="font-semibold">“NEON”</span> out now.
            </p>

            {/* blocks — re-stagger on each rebuild */}
            <motion.div key={`blocks-${buildKey}`} variants={parent} initial="hidden" animate="show">
              <motion.div variants={child} className="mt-4 rounded-2xl border border-line bg-surface p-3 shadow-soft">
                <div className="flex items-center gap-3">
                  <img src={PRODUCT_IMG} alt="" className="h-11 w-11 rounded-lg object-cover" loading="lazy" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[13px] font-semibold text-ink">Neon — {displayName}</p>
                    <p className="text-[11px] text-inkMuted">Now playing on Spotify</p>
                  </div>
                  <span className="flex h-6 items-end gap-[2px]">
                    {WAVE.map((h, i) => (
                      <span key={i} className="wave-bar w-[3px] rounded-full bg-lime" style={{ height: `${h}%`, animationDelay: `${i * 0.12}s` }} />
                    ))}
                  </span>
                </div>
              </motion.div>

              <motion.div variants={child} className="mt-3 overflow-hidden rounded-2xl border border-line bg-surface shadow-soft">
                <div className="relative h-28 w-full">
                  <img src={VIDEO_IMG} alt="Live set" className="h-full w-full object-cover" loading="lazy" />
                  <div className="absolute inset-0 bg-black/20" />
                  <span className="absolute left-1/2 top-1/2 grid h-11 w-11 -translate-x-1/2 -translate-y-1/2 place-items-center rounded-full bg-lime text-limeText">
                    <Play size={18} className="ml-0.5 fill-current" />
                  </span>
                  <span className="absolute bottom-2 left-2 rounded-md bg-black/60 px-2 py-0.5 text-[10px] font-medium text-white">
                    Live at Berghain · 12:04
                  </span>
                </div>
              </motion.div>

              <motion.div variants={child} className="mt-3 flex items-center gap-3 rounded-2xl border border-line bg-surfaceAlt p-3">
                <span className="grid h-9 w-9 place-items-center rounded-full bg-lime text-limeText">
                  <Calendar size={16} />
                </span>
                <div className="flex-1">
                  <p className="text-[13px] font-semibold text-ink">Book a studio session</p>
                  <p className="text-[11px] text-inkMuted">Mon–Fri · from €120/hr</p>
                </div>
                <ArrowUpRight size={16} className="text-inkMuted" />
              </motion.div>

              <motion.div variants={child} className="mt-3 flex items-center gap-3 rounded-2xl border border-line bg-surface p-3 shadow-soft">
                <img src={PRODUCT_IMG} alt="Vinyl" className="h-12 w-12 rounded-lg object-cover" loading="lazy" />
                <div className="flex-1">
                  <p className="text-[13px] font-semibold text-ink">NEON LP — Exclusive Vinyl</p>
                  <p className="text-[11px] text-inkMuted">Limited pressing · €34</p>
                </div>
                <span className="grid h-8 w-8 place-items-center rounded-full bg-lime text-limeText">
                  <ShoppingBag size={14} />
                </span>
              </motion.div>

              {["Latest EP — stream everywhere", "Merch store", "Tour dates 2026"].map((label) => (
                <motion.a
                  key={label}
                  variants={child}
                  href="#"
                  onClick={(e) => e.preventDefault()}
                  className="group mt-3 flex items-center justify-between rounded-2xl border border-line bg-surfaceAlt px-4 py-3 text-[13px] font-semibold text-ink transition-colors hover:border-lime"
                >
                  {label}
                  <ArrowUpRight size={15} className="text-inkMuted transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:text-lime" />
                </motion.a>
              ))}
            </motion.div>

            <p className="mt-5 text-center text-[10px] font-medium uppercase tracking-[0.2em] text-inkMuted">
              Powered by BREELINK
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
